import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { CockpitErrorNotice } from "@/components/cockpit/CockpitErrorNotice";
import { CockpitSkeletonBlock } from "@/components/cockpit/CockpitSkeleton";
import {
  getFollowupPreference,
  setFollowupPreference,
} from "@/lib/email-followup.functions";

export const Route = createFileRoute("/_cockpit/cockpit/profil")({
  head: () => ({
    meta: [
      { title: "Profil & E-Mails – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitProfilePage,
});

function CockpitProfilePage() {
  const queryClient = useQueryClient();
  const fetchPreference = useServerFn(getFollowupPreference);
  const savePreference = useServerFn(setFollowupPreference);

  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit", "followup-preference"],
    queryFn: () => fetchPreference(),
  });

  const mutation = useMutation({
    mutationFn: (optedOut: boolean) => savePreference({ data: { optedOut } }),
    onSuccess: (res: any) => {
      toast.success(
        res.optedOut
          ? "Sie erhalten künftig keine Begleitmails mehr."
          : "Sie erhalten Begleitmails wieder.",
      );
      void queryClient.invalidateQueries({ queryKey: ["cockpit", "followup-preference"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Änderung fehlgeschlagen."),
  });

  return (
    <CockpitPageShell width="reading">
      <CockpitPageHeader
        title="Profil & E-Mails"
        description="Hier legen Sie fest, welche begleitenden E-Mails Sie von uns erhalten."
      />

      {isLoading ? <CockpitSkeletonBlock /> : null}
      <CockpitErrorNotice error={error} title="Einstellungen konnten nicht geladen werden" />

      {data && (
        <section className="rounded-lg border border-border bg-card p-5 md:p-6">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="font-display text-lg font-extrabold text-primary">Begleitmails</h2>
              <p className="mt-1 font-body text-sm text-muted-foreground">
                Begleitmails sind Hinweise und Impulse zu Ihrem laufenden Modul, etwa zum Start
                einer neuen Phase. E-Mails zu Ihrem Zugang, zum Passwort und zu laufenden Vorgängen
                wie Fragebögen oder Übergaben von Zugangsdaten erhalten Sie unabhängig davon weiterhin.
              </p>
              {data.email && (
                <p className="mt-2 font-body text-sm text-muted-foreground">
                  Versand an: <span className="text-foreground">{data.email}</span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-md border border-border bg-secondary/40 p-4">
            <Switch
              id="followup-opt-in"
              checked={!data.optedOut}
              disabled={mutation.isPending}
              onCheckedChange={(checked) => mutation.mutate(!checked)}
            />
            <label
              htmlFor="followup-opt-in"
              className="font-display text-sm font-extrabold text-foreground"
            >
              Ich möchte Begleitmails erhalten.
            </label>
            <span
              aria-live="polite"
              className="flex items-center gap-1.5 font-body text-xs text-muted-foreground"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> wird
                  gespeichert …
                </>
              ) : (
                "Änderungen werden sofort gespeichert."
              )}
            </span>
          </div>
        </section>
      )}
    </CockpitPageShell>
  );
}
